import * as React from 'react';
import { cx } from 'linaria';
import { Typography, useTheme, makeTestId, states, ThemeProvider } from '../index';
import * as Polymorphic from '../index';
import * as styles from './button.styles';

export type ButtonVariant = 'primary' | 'outline' | 'text' | 'destruct';
export interface ButtonProps {
    as?: React.ElementType;
    /**
     *  Size of the button
     *  @default medium
     */
    size?: 'small' | 'medium';
    /**
     *  Variant of the button
     *  @default primary
     */
    variant?: ButtonVariant;
    /**
     *  Occupies full width
     *  @default false
     */
    stretched?: boolean;
    /**
     *  Displays button in loading state with a spinner
     */
    loading?: boolean;
    /**
     *  Add additional loading text along with the spinner
     */
    loadingText?: string | React.ReactElement;
    /**
     *  Icon or any Element that needs to be displayed before the button text
     */
    startSlot?: React.ReactElement;
    /**
     *  Icon or any Element that needs to be displayed after the button text
     */
    endSlot?: React.ReactElement;
    /**
     *  Test id for unit and E2E tests
     */
    testId?: string;
    /**
     *  Theme override
     */
    themeOverride?: 'light' | 'dark';
}

const DEFAULT_TAG = 'button';

export type ButtonComponent = Polymorphic.ForwardRefComponent<typeof DEFAULT_TAG, ButtonProps>;

export const Button: ButtonComponent = React.forwardRef((props, ref) => {
    const {
        testId,
        as: Component = DEFAULT_TAG,
        size = 'medium',
        variant = 'primary',
        stretched,
        disabled,
        loading,
        loadingText,
        startSlot,
        endSlot,
        children,
        themeOverride,
        ...passThroughProps
    } = props;
    const currentTheme = useTheme();
    const theme = themeOverride ? themeOverride : currentTheme;
    const actualTheme = variant === 'primary' || variant === 'destruct' ? 'dark' : theme;

    const childrenArray = React.Children.toArray(children);
    const isElementOnly = childrenArray.length === 1 && React.isValidElement(childrenArray[0]);

    const childMarkup = React.useMemo(() => {
        // Intended for buttons that contain only an icon
        if (isElementOnly) {
            const childProps = (children as React.ReactElement).props;
            return React.cloneElement(children as React.ReactElement, { color: 'currentColor', ...childProps });
        }

        return (
            <Typography
                as="span"
                className={cx(styles.text, loading && styles.textPosition, loading && styles.hide)}
                variant="button"
                color="inherit"
            >
                {children}
            </Typography>
        );
    }, [loading, children, isElementOnly]);

    const loadingMarkup = loading ? (
        <div
            className={cx(styles.spinnerWrapper, styles.textPosition)}
            data-testid={makeTestId(testId, 'loading-spinner')}
        >
            {loadingText}
        </div>
    ) : null;

    const className = cx(
        passThroughProps.className,
        styles.root,
        styles.sizes[size],
        styles.variants[variant],
        stretched && styles.fullWidth,
        isElementOnly && styles.iconOnly,
        states,
    );

    let rendered;
    if ((Component as React.ElementType) != 'button') {
        rendered = (
            <Component ref={ref} {...passThroughProps} className={className} data-testid={testId}>
                {startSlot}
                {childMarkup}
                {endSlot}
            </Component>
        );
    } else {
        rendered = (
            <Component
                type="button"
                ref={ref}
                {...passThroughProps}
                className={className}
                disabled={disabled || loading}
                aria-busy={loading}
                data-testid={testId}
            >
                {loadingMarkup}
                {startSlot}
                {childMarkup}
                {endSlot}
            </Component>
        );
    }
    return <ThemeProvider theme={actualTheme}>{rendered}</ThemeProvider>;
});

Button.displayName = 'Button';

export interface UnstyledButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    testId?: string;
}

export const UnstyledButton = React.forwardRef<HTMLButtonElement, UnstyledButtonProps>((props, ref) => {
    const { testId, className, ...passThroughProps } = props;
    return (
        <button
            type="button"
            {...passThroughProps}
            className={cx(styles.unstyled, className)}
            ref={ref}
            data-testid={testId}
        />
    );
});

UnstyledButton.displayName = 'UnstyledButton';
